import { useSyncExternalStore } from "react";
import type { CustomerOrigin } from "@yatta/domain";
import { DEMO_RESET_EVENT } from "../lib/demo-session";

export type CanonicalCustomerState =
  | "ACTIVE"
  | "AVAILABLE"
  | "CURRENT"
  | "HIGH"
  | "IN_REVIEW"
  | "LOW"
  | "MEDIUM"
  | "NOT_ACTIVATED"
  | "NOT_CONTRACTED"
  | "NOT_STARTED"
  | "OVERDUE"
  | "PENDING"
  | "REGISTERED"
  | "SUSPENDED"
  | "WAITING_PERIOD";

export type FakeCustomerRecord = {
  id: string;
  displayName: string;
  holderName: string;
  kind: "HOUSEHOLD" | "LEGAL_ENTITY";
  identifier: string;
  contact: string;
  financialStatus: "CURRENT" | "OVERDUE";
  riskLevel: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  contracts: number;
  subscriptions: number;
  agreements: number;
  openInvoices: number;
  outstandingCents: string;
  openTasks: number;
  customerOrigin: CustomerOrigin;
  responsibleSellerId: string;
  registrationStatus: "REGISTERED" | "SUSPENDED";
  contractStatus: "ACTIVE" | "IN_REVIEW" | "NOT_CONTRACTED";
  accessStatus: "AVAILABLE" | "NOT_STARTED";
  benefitStatus: "ACTIVE" | "PENDING" | "NOT_ACTIVATED";
  funeralStatus: "ACTIVE" | "SUSPENDED" | "WAITING_PERIOD" | "NOT_CONTRACTED";
  telemedicineStatus: "ACTIVE" | "NOT_CONTRACTED";
  dependents: number;
  lastEventAt?: string;
};

export type FakeCustomerListRow = {
  id: string;
  name: string;
  holder: string;
  phone: string;
  funeral: string;
  telemedicine: string;
  seller: string;
  balance: string;
};

const sellerNames: Record<string, string> = {
  "1": "Vendedor Demo 01",
  "2": "Vendedor Demo 02",
  "3": "Vendedor Demo 03",
  "seller-demo-1": "Vendedor Demo 01",
  "seller-demo-2": "Vendedor Demo 02",
  "seller-demo-3": "Vendedor Demo 03",
};

const sellerIdsByName = Object.fromEntries(
  Object.entries(sellerNames).map(([id, name]) => [name, id]),
);

const seedRecords: readonly FakeCustomerRecord[] = [
  {
    id: "c360-1",
    displayName: "Família Oliveira",
    holderName: "Carlos Oliveira",
    kind: "HOUSEHOLD",
    identifier: "***0182",
    contact: "(41) 9****-0182",
    financialStatus: "CURRENT",
    riskLevel: "LOW",
    contracts: 2,
    subscriptions: 2,
    agreements: 0,
    openInvoices: 1,
    outstandingCents: "9890",
    openTasks: 1,
    customerOrigin: "PREEXISTING",
    responsibleSellerId: "1",
    registrationStatus: "REGISTERED",
    contractStatus: "ACTIVE",
    accessStatus: "NOT_STARTED",
    benefitStatus: "ACTIVE",
    funeralStatus: "ACTIVE",
    telemedicineStatus: "ACTIVE",
    dependents: 1,
    lastEventAt: "2026-09-20T14:30:00Z",
  },
  {
    id: "c360-2",
    displayName: "Família Santos",
    holderName: "Márcia Santos",
    kind: "HOUSEHOLD",
    identifier: "***7740",
    contact: "(41) 9****-7740",
    financialStatus: "CURRENT",
    riskLevel: "MEDIUM",
    contracts: 1,
    subscriptions: 0,
    agreements: 0,
    openInvoices: 0,
    outstandingCents: "0",
    openTasks: 2,
    customerOrigin: "NEW_ACQUISITION",
    responsibleSellerId: "2",
    registrationStatus: "REGISTERED",
    contractStatus: "IN_REVIEW",
    accessStatus: "AVAILABLE",
    benefitStatus: "PENDING",
    funeralStatus: "WAITING_PERIOD",
    telemedicineStatus: "NOT_CONTRACTED",
    dependents: 0,
    lastEventAt: "2026-09-20T13:10:00Z",
  },
  {
    id: "c360-3",
    displayName: "Família Almeida",
    holderName: "João Almeida",
    kind: "HOUSEHOLD",
    identifier: "***2119",
    contact: "(41) 9****-2119",
    financialStatus: "OVERDUE",
    riskLevel: "MEDIUM",
    contracts: 1,
    subscriptions: 1,
    agreements: 0,
    openInvoices: 1,
    outstandingCents: "5900",
    openTasks: 1,
    customerOrigin: "PREEXISTING",
    responsibleSellerId: "3",
    registrationStatus: "REGISTERED",
    contractStatus: "ACTIVE",
    accessStatus: "AVAILABLE",
    benefitStatus: "ACTIVE",
    funeralStatus: "SUSPENDED",
    telemedicineStatus: "ACTIVE",
    dependents: 0,
    lastEventAt: "2026-09-19T16:05:00Z",
  },
];

const STORAGE_KEY = "yatta:cruzeiro-life:demo-customers";

function readPersistedRecords(): FakeCustomerRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as FakeCustomerRecord[]) : [];
  } catch {
    return [];
  }
}

const records: FakeCustomerRecord[] = readPersistedRecords();
const listeners = new Set<() => void>();
let storeVersion = 0;

function notifyStoreChanged() {
  storeVersion += 1;
  listeners.forEach((listener) => listener());
}

function persistRecords() {
  if (typeof window === "undefined") return;
  try {
    if (records.length === 0) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch {
    // A restricted browser storage must not prevent the in-memory demo flow.
  }
}

export function subscribeFakeCustomerStore(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getFakeCustomerStoreVersion() {
  return storeVersion;
}

export function useFakeCustomerStoreVersion() {
  return useSyncExternalStore(
    subscribeFakeCustomerStore,
    getFakeCustomerStoreVersion,
    getFakeCustomerStoreVersion,
  );
}

export function listFakeCustomerRecords(): FakeCustomerRecord[] {
  const seededIds = new Set(seedRecords.map((record) => record.id));
  const storedById = new Map(records.map((record) => [record.id, record]));
  return [
    ...seedRecords.map((record) => storedById.get(record.id) ?? record),
    ...records.filter((record) => !seededIds.has(record.id)),
  ];
}

export function resolveSellerName(sellerId: string | undefined) {
  return (sellerId && sellerNames[sellerId]) || "Vendedor não atribuído";
}

function resolveSellerId(sellerId: string | undefined, sellerName: string | undefined) {
  return sellerId || (sellerName && sellerIdsByName[sellerName]) || "";
}

export function customerStatusLabel(status: string | undefined) {
  switch (status) {
    case "ACTIVE":
      return "ATIVO";
    case "AVAILABLE":
      return "DISPONÍVEL";
    case "CURRENT":
      return "Em dia";
    case "IN_REVIEW":
      return "EM ANÁLISE";
    case "NOT_ACTIVATED":
      return "NÃO ATIVADO";
    case "NOT_CONTRACTED":
    case "NONE":
      return "NÃO CONTRATADO";
    case "NOT_STARTED":
      return "NÃO INICIADO";
    case "OVERDUE":
      return "Vencido";
    case "PENDING":
      return "PENDENTE";
    case "REGISTERED":
      return "CADASTRADO";
    case "SUSPENDED":
      return "SUSPENSO";
    case "WAITING_PERIOD":
      return "CARÊNCIA";
    default:
      return status || "NÃO INFORMADO";
  }
}

export function fakeCustomerToListRow(record: FakeCustomerRecord): FakeCustomerListRow {
  return {
    id: record.id,
    name: record.displayName,
    holder: record.holderName,
    phone: record.contact,
    funeral: customerStatusLabel(record.funeralStatus),
    telemedicine: customerStatusLabel(record.telemedicineStatus),
    seller: resolveSellerName(record.responsibleSellerId),
    balance: customerStatusLabel(record.financialStatus),
  };
}

export function fakeCustomerDashboardMetrics() {
  const rows = listFakeCustomerRecords().map(fakeCustomerToListRow);
  return [
    { label: "Famílias ativas", value: String(rows.length), delta: "Base sintética" },
    {
      label: "Planos funerários",
      value: String(rows.filter((row) => row.funeral === "ATIVO").length),
      delta: "Contratos ativos",
    },
    {
      label: "Telemedicina",
      value: String(rows.filter((row) => row.telemedicine === "ATIVO").length),
      delta: "Vidas ativas",
    },
    { label: "Recebido no mês", value: "R$ 42.840", delta: "Demo sintética" },
    {
      label: "Inadimplência",
      value: String(rows.filter((row) => row.balance === "Vencido").length),
      delta: "Clientes vencidos",
    },
    { label: "Comissão elegível", value: "R$ 4.280", delta: "Demo sintética" },
  ];
}

if (typeof window !== "undefined") {
  window.addEventListener(DEMO_RESET_EVENT, () => resetFakeCustomerStore());
}

export function resetFakeCustomerStore() {
  if (records.length === 0) {
    persistRecords();
    return;
  }
  records.splice(0, records.length);
  persistRecords();
  notifyStoreChanged();
}

function maskPhone(phone: string) {
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 4
    ? `(${digits.slice(0, 2)}) 9****-${digits.slice(-4)}`
    : "Contato informado";
}

export function createFakeCustomerRecord(input: {
  fullName: string;
  cpf: string;
  phone: string;
  sellerId?: string;
  sellerName?: string;
  customerOrigin: CustomerOrigin;
  dependentCount: number;
}) {
  const id = `c360-demo-${Date.now()}-${records.length + 1}`;
  const record: FakeCustomerRecord = {
    id,
    displayName: input.fullName.trim(),
    holderName: input.fullName.trim(),
    kind: "HOUSEHOLD",
    identifier: `***${input.cpf.replace(/\D/g, "").slice(-4)}`,
    contact: maskPhone(input.phone),
    financialStatus: "CURRENT",
    riskLevel: "LOW",
    contracts: 0,
    subscriptions: 0,
    agreements: 0,
    openInvoices: 0,
    outstandingCents: "0",
    openTasks: 0,
    customerOrigin: input.customerOrigin,
    responsibleSellerId: resolveSellerId(input.sellerId, input.sellerName),
    registrationStatus: "REGISTERED",
    contractStatus: "NOT_CONTRACTED",
    accessStatus: "NOT_STARTED",
    benefitStatus: "NOT_ACTIVATED",
    funeralStatus: "NOT_CONTRACTED",
    telemedicineStatus: "NOT_CONTRACTED",
    dependents: input.dependentCount,
  };
  records.push(record);
  persistRecords();
  notifyStoreChanged();
  return record;
}

export function getFakeCustomerRecord(id: string) {
  return listFakeCustomerRecords().find((record) => record.id === id) ?? null;
}

export function updateFakeCustomerRecord(
  id: string,
  changes: Pick<FakeCustomerRecord, "holderName" | "customerOrigin" | "responsibleSellerId">,
) {
  const current = getFakeCustomerRecord(id);
  if (!current) return false;
  const updated: FakeCustomerRecord = {
    ...current,
    ...changes,
    displayName: changes.holderName.trim(),
    lastEventAt: new Date().toISOString(),
  };
  const existingIndex = records.findIndex((record) => record.id === id);
  if (existingIndex === -1) records.push(updated);
  else records[existingIndex] = updated;
  persistRecords();
  notifyStoreChanged();
  return true;
}
